
// src/components/shared/GoogleAuth.js
import React, { useContext } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { ConfigContext } from './ConfigContext';
import axios from 'axios';
import { setTokens, scheduleProactiveRefresh } from '../../utils/authTokens';

export const persistGoogleUser = (user, setAuth, backendUrl) => {
    localStorage.setItem('userId', user._id);
    localStorage.setItem('userRole', user.role);
    localStorage.setItem('username', user.username);
    localStorage.setItem('companyId', user.companyId);
    setTokens({ accessToken: user.accessToken, refreshToken: user.refreshToken });
    scheduleProactiveRefresh(backendUrl);
    setAuth({
        username: user.username,
        userRole: user.role,
    });
    // `companies` is only present when the login route detected more than one
    // membership — the caller is already validly signed into a default
    // company above, this just asks them to confirm/pick before landing on
    // the dashboard.
    return user.companies && user.companies.length > 1 ? '/select-company' : '/';
};

const GoogleAuth = ({ setAuth }) => {
    const config = useContext(ConfigContext);

    // Return early if config is not available
    if (!config || !config.backendUrl) {
        return <div>Loading configuration...</div>;
    }

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            // Send the Google token to your backend
            const response = await axios.post(`${config.backendUrl}/auth/google`, {
                token: credentialResponse.credential,
            });

            const user = response.data;

            window.location.href = persistGoogleUser(user, setAuth, config.backendUrl);
        } catch (error) {
            console.error('Google authentication error:', error);
            alert(error.response?.data?.error || 'Error during Google login.');
        }
    };

    const handleGoogleError = () => {
        console.error('Google Sign-In was unsuccessful');
    };

    return (
        <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            useOneTap
            text="continue_with"
            shape="pill"
            theme="outline"
            size="large"
            width="100%"
        />
    );
};

// Wrap component with Google OAuth Provider that gets client ID from context
const GoogleAuthProvider = ({ children }) => {
    const config = useContext(ConfigContext);

    // Check if config exists and has googleClientId before rendering the provider
    if (!config || !config.googleClientId) {
        console.error('Google client ID is missing from configuration');
        return <>{children}</>; // Still render children but without the provider
    }

    return (
        <GoogleOAuthProvider clientId={config.googleClientId}>
            {children}
        </GoogleOAuthProvider>
    );
};

export { GoogleAuth, GoogleAuthProvider };
